import { forwardRef } from 'react'

import { LinkedInLogoIcon } from '@radix-ui/react-icons'
import { useNavigate, useParams , Link } from '@tanstack/react-router'
import { BotIcon, ForwardIcon, HandshakeIcon, MapPinIcon, SendIcon, UserSearchIcon } from 'lucide-react'

import { CountryFlag } from '@/components/country-flag'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/text'
import { useBulkActions } from '@/hooks/use-bulk-actions'
import { cn } from '@/lib/utils'
import { JobBoardApplicationWithStageId } from '@/types/application/job-board'
import { getDistanceToNowStrict } from '@/utils/date'
import { ApplicantLocationProps, formatApplicantLocation, getFullName, getFullNameInitial } from '@/utils/string'

import { CardFooter } from './card-footer'
import { Availability } from './components/availability'
import ExpectedSalary from './components/expected-salary'
import YearOfExperience from './components/year-of-experience'
import { isBulkableOrDraggable } from './utils'

import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  application: JobBoardApplicationWithStageId
  color: string
  overlay?: boolean
}

const Card = forwardRef<HTMLDivElement, CardProps>(({ application, color, overlay, style, ...props }, ref) => {
  const { isOpen, selected, setSelected } = useBulkActions()
  const { jobId } = useParams({ from: '/_authenticated/jobs_/$jobId' })
  const navigate = useNavigate({ from: '/jobs/$jobId' })
  const { applicant } = application

  const isInteractive = isBulkableOrDraggable(application.stageName)

  let sourceIcon = null
  let sourceTooltip = null

  switch (application.typeOfSource) {
    case 'SOURCING':
      sourceIcon = <UserSearchIcon size={12} />
      sourceTooltip = 'Sourced candidate'
      break
    case 'DIRECT_APPLY':
      sourceIcon = <SendIcon size={12} />
      sourceTooltip = 'Direct application'
      break
    case 'COOPTATION':
      sourceIcon = <HandshakeIcon size={12} />
      sourceTooltip = 'Coopted candidate'
      break
    case 'INTERNAL_FORWARD':
      sourceIcon = <ForwardIcon size={12} />
      sourceTooltip = 'Internal forward'
      break
    case 'AI':
      sourceIcon = <BotIcon size={12} />
      sourceTooltip = 'AI - Suggested candidate'
      break
    default:
      sourceIcon = <UserSearchIcon size={12} />
      sourceTooltip = 'Sourced candidate'
      break
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded border-l-4 bg-white shadow-sm transition-shadow shrink-0 w-full',
        overlay && 'shadow-2xl cursor-grabbing',
        isInteractive && 'active:cursor-grabbing cursor-grab',
      )}
      ref={ref}
      style={{ borderLeftColor: color, ...style }}
      {...props}
      role="button"
    >
      <div
        className="flex gap-x-2 p-3"
        onClick={async () => {
          if (isOpen) return
          await navigate({
            params: { applicationId: application.id, jobId },
            search: { view: 'application' },

            to: '/jobs/$jobId/applications/$applicationId',
          })
        }}
      >
        <div className="flex gap-x-2">
          <Avatar>
            <AvatarFallback>{getFullNameInitial(applicant)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-x-1">
              <Tooltip>
                <TooltipTrigger asChild>{sourceIcon}</TooltipTrigger>
                <TooltipContent side="top">{sourceTooltip}</TooltipContent>
              </Tooltip>
              <Link
                className="hover:underline"
                key={applicant.id}
                params={{ applicantId: applicant.id }}
                search={{ view: 'details' }}
                target="_blank"
                to="/applicants/$applicantId"
              >
                <Text size="sm" weight="medium">
                  {getFullName(applicant)}
                </Text>
              </Link>
              <CountryFlag code={applicant.nationality.id} name={applicant.nationality.name} />

              {applicant.linkedinUrl && (
                <a
                  href={applicant.linkedinUrl}
                  onClick={(e) => e.stopPropagation()}
                  rel="noreferrer"
                  target="_blank"
                  title="See LinkedIn profile"
                >
                  <LinkedInLogoIcon className="text-linkedin" />
                </a>
              )}
            </div>
            <div className="pb-3">
              <div className="flex items-center gap-x-1">
                <MapPinIcon size={12} />
                {formatApplicantLocation(applicant as ApplicantLocationProps) ? (
                  <Text size="sm" weight="light">
                    {formatApplicantLocation(applicant as ApplicantLocationProps)}
                  </Text>
                ) : (
                  <Text size="xs" variant="muted" weight="light">
                    No location
                  </Text>
                )}
              </div>
            </div>
            <Text size="sm" weight="light">
              {applicant.currentJob} {applicant.currentCompany && `@${applicant.currentCompany}`}
            </Text>
          </div>
        </div>
        <div
          className={cn(
            'ml-auto shrink-0 transition-transform ease-in-out',
            isOpen && isInteractive && '-translate-x-6',
          )}
        >
          <Text size="xs">{getDistanceToNowStrict(application.createdAt)}</Text>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-x-2 px-4 pb-4">
        <YearOfExperience value={applicant.yearOfExperience} />
        <Availability availability={application.availability} />
        <ExpectedSalary expectedSalary={application.expectedSalary} />
      </div>

      <CardFooter application={application} />

      {isInteractive && (
        <Label
          className={cn(
            'absolute inset-0 pointer-events-none p-3 opacity-0 transition-opacity bg-background/50 border border-transparent rounded-r text-right',
            isOpen && 'cursor-pointer opacity-100 pointer-events-auto',
            selected.find((id) => id === `${application.id}`) && 'bg-primary/10 border-primary',
          )}
          htmlFor={`${application.id}`}
        >
          <Checkbox
            checked={selected.includes(`${application.id}`)}
            className="pointer-events-none"
            id={`${application.id}`}
            onCheckedChange={(e) =>
              e
                ? setSelected([...selected, `${application.id}`])
                : setSelected(selected.filter((sel) => sel !== `${application.id}`))
            }
          />
        </Label>
      )}
    </div>
  )
})

Card.displayName = 'Card'

function CardSkeleton() {
  return <Skeleton className="h-24 w-full" />
}

export { Card, CardSkeleton }
